import React from 'react';
import styled from '@emotion/styled';
import type { AppTheme } from '@/theme/index';

interface LogoProps {
  isOpen: boolean;
  theme: AppTheme;
  onToggle?: () => void;
}

const LogoWrapper = styled.div<{ theme: AppTheme; isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? 'space-between' : 'center')};
  padding: ${({ theme }) => theme.spacing[4]}px;
`;

const LogoSvg = styled.svg<{ theme: AppTheme; isOpen: boolean }>`
  width: ${props => (props.isOpen ? '80px' : '24px')};
  height: 24px;
  flex-shrink: 0;

  path {
    fill: ${({ theme }) => theme.colors.button.text.tertiaryBrand};
  }
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
  display: ${props => (props.isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

export const Logo: React.FC<LogoProps> = ({ isOpen, theme, onToggle }) => {
  return (
    <LogoWrapper theme={theme} isOpen={isOpen}>
      {isOpen ? (
        <>
          <LogoSvg
            theme={theme}
            isOpen={isOpen}
            viewBox="0 0 80 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_5439_1073)">
              <path d="M3.65358 20.7801H0V6.77256C0 4.37368 1.29654 3 3.5738 3C5.85107 3 7.12101 4.37368 7.12101 6.77256V9.06317C7.12101 10.2203 6.59241 11.1102 5.79789 11.594C6.75197 11.9966 7.33376 13.0489 7.33376 14.3955V16.9534C7.33376 19.2711 6.27326 20.7801 3.65358 20.7801ZM4.84376 6.90451C4.84376 5.66616 4.42153 5.10113 3.5738 5.10113C2.72606 5.10113 2.27726 5.66616 2.27726 6.90451V10.7583H3.41422C4.36834 10.7583 4.84376 10.0579 4.84376 8.95487V6.90451ZM5.0565 14.5038C5.0565 13.4007 4.6077 12.7004 3.65358 12.7004H2.27726V18.6552H3.65358C4.6077 18.6552 5.0565 18.009 5.0565 16.906V14.5038Z" />
              <path d="M13.2988 20.7803C10.6792 20.7803 9.67188 19.2713 9.67188 16.9536V7.04007C9.67188 4.7224 10.6792 3.21338 13.2988 3.21338H15.3367V5.36864H13.3521C12.3979 5.36864 11.9491 6.04195 11.9491 7.14496V10.6468H14.9411V12.8021H11.9491V16.8961C11.9491 17.9991 12.3979 18.6217 13.3521 18.6217H15.3367V20.7769H13.2988V20.7803Z" />
              <path d="M21.2989 20.7798C18.6792 20.7798 17.6719 19.2708 17.6719 16.9532V3.21631H19.9491V10.0323V16.8483C19.9491 17.9513 20.3979 18.628 21.3521 18.628H23.3367V20.7832H21.2989V20.7798Z" />
              <path d="M29.3028 20.7798C26.6831 20.7798 25.6758 19.2708 25.6758 16.9532V3.21631H27.953V16.8483C27.953 17.9513 28.4018 18.628 29.356 18.628H31.3407V20.7832H29.3028V20.7798Z" />
              <path d="M39.4207 20.9966C37.1434 20.9966 35.8203 19.6229 35.8203 17.2241V6.77256C35.8203 4.37368 37.1434 3 39.4207 3C41.698 3 42.9946 4.37368 42.9946 6.77256V8.7654H40.7173V6.90789C40.7173 5.66955 40.2685 5.10451 39.4207 5.10451C38.573 5.10451 38.0976 5.66955 38.0976 6.90789V17.0921C38.0976 18.3305 38.573 18.8955 39.4207 18.8955C40.2685 18.8955 40.7173 18.3305 40.7173 17.0921V15.2346H42.9946V17.2275C42.9946 19.6263 41.698 21 39.4207 21V20.9966Z" />
              <path d="M48.959 20.7798C46.3396 20.7798 45.332 19.2708 45.332 16.9532V3.21631H47.6095V16.8483C47.6095 17.9513 48.0582 18.628 49.0122 18.628H50.9969V20.7832H48.959V20.7798Z" />
              <path d="M57.0427 20.9966C54.7657 20.9966 53.3359 19.6229 53.3359 17.2241V6.77256C53.3359 4.37368 54.7657 3 57.0427 3C59.3201 3 60.7494 4.37368 60.7494 6.77256V17.2241C60.7494 19.6229 59.3201 20.9966 57.0427 20.9966ZM58.4724 6.90451C58.4724 5.66616 57.8904 5.10113 57.0427 5.10113C56.1949 5.10113 55.6134 5.66616 55.6134 6.90451V17.0887C55.6134 18.3271 56.1949 18.8921 57.0427 18.8921C57.8904 18.8921 58.4724 18.3271 58.4724 17.0887V6.90451Z" />
              <path d="M66.7395 20.9964C64.4621 20.9964 63.0859 19.6227 63.0859 17.2238V3.21631H65.3634V17.0885C65.3634 18.3269 65.8918 18.8919 66.7395 18.8919C67.5872 18.8919 68.1157 18.3269 68.1157 17.0885V3.21631H70.3931V17.2238C70.3931 19.6227 69.0169 20.9964 66.7395 20.9964Z" />
              <path d="M76.4638 20.7798H72.7305V3.21631H76.4638C79.0836 3.21631 80.1439 4.72533 80.1439 7.043V16.9565C80.1439 19.2742 79.0866 20.7832 76.4638 20.7832V20.7798ZM77.8665 7.14789C77.8665 6.04488 77.4178 5.3445 76.4638 5.3445H75.0075V18.6517H76.4638C77.4178 18.6517 77.8665 18.0054 77.8665 16.9024V7.14789Z" />
            </g>
            <defs>
              <clipPath id="clip0_5439_1073">
                <rect width="80" height="24" fill="white" />
              </clipPath>
            </defs>
          </LogoSvg>
          <ToggleButton isOpen={isOpen} onClick={onToggle}>
            <img src="/icons/slide_in.svg" alt="Toggle sidebar" />
          </ToggleButton>
        </>
      ) : (
        <LogoSvg
          theme={theme}
          isOpen={isOpen}
          viewBox="0 0 8 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3.65358 20.7801H0V6.77256C0 4.37368 1.29654 3 3.5738 3C5.85107 3 7.12101 4.37368 7.12101 6.77256V9.06317C7.12101 10.2203 6.59241 11.1102 5.79789 11.594C6.75197 11.9966 7.33376 13.0489 7.33376 14.3955V16.9534C7.33376 19.2711 6.27326 20.7801 3.65358 20.7801ZM4.84376 6.90451C4.84376 5.66616 4.42153 5.10113 3.5738 5.10113C2.72606 5.10113 2.27726 5.66616 2.27726 6.90451V10.7583H3.41422C4.36834 10.7583 4.84376 10.0579 4.84376 8.95487V6.90451ZM5.0565 14.5038C5.0565 13.4007 4.6077 12.7004 3.65358 12.7004H2.27726V18.6552H3.65358C4.6077 18.6552 5.0565 18.009 5.0565 16.906V14.5038Z" />
        </LogoSvg>
      )}
    </LogoWrapper>
  );
};
